/**
 * Manager Payments — admin/finance wanaona invoices zote na wanaweza kuunda invoice mpya.
 */
import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { membersAPI, paymentsAPI, paymentSlipsAPI } from '../../services/api';

const AWTF_FEE = 50000;
const CURRENT_YEAR = new Date().getFullYear();
const ITEMS_PER_PAGE = 25;

function fmtTZS(n) {
  const abs = Math.abs(Number(n) || 0);
  if (abs >= 1e9) return `TZS ${(n / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `TZS ${(n / 1e6).toFixed(2)}M`;
  return `TZS ${Number(n || 0).toLocaleString()}`;
}

function isPaid(p) {
  return String(p?.paystatus || '').toUpperCase() === 'PAID';
}
function statusBadge(p) {
  return isPaid(p) ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700';
}

export default function ManagerPayments() {
  const [activeTab, setActiveTab] = useState('invoices'); // invoices | direct-deposit
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [feeYear, setFeeYear] = useState(CURRENT_YEAR);
  const [availableYears, setAvailableYears] = useState([CURRENT_YEAR]);
  const [summary, setSummary] = useState({ total_invoiced: 0, total_paid: 0, total_outstanding: 0 });

  // Create invoice modal
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [newInvoice, setNewInvoice] = useState(null);
  const [form, setForm] = useState({ member_id: '', fee_year: CURRENT_YEAR, fee: AWTF_FEE, duedate: '' });
  const [memberSearch, setMemberSearch] = useState('');
  const [memberSuggestions, setMemberSuggestions] = useState([]);
  const [memberName, setMemberName] = useState('');

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await paymentsAPI.list(currentPage, ITEMS_PER_PAGE, status, feeYear);
      setPayments(resp.invoices || []);
      setTotalPages(resp.total_pages || 1);
      setTotalCount(resp.total || 0);
      if (resp.available_years?.length) setAvailableYears(resp.available_years);
      if (resp.summary) setSummary(resp.summary);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load payments.');
    } finally {
      setLoading(false);
    }
  }, [status, feeYear, currentPage]);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);
  useEffect(() => { setCurrentPage(1); }, [status, feeYear]);

  // Member search autocomplete
  useEffect(() => {
    if (memberSearch.length < 2) { setMemberSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const resp = await membersAPI.list(1, 10, memberSearch);
        setMemberSuggestions(resp.members || []);
      } catch { setMemberSuggestions([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [memberSearch]);

  async function handleCreate(e) {
    e.preventDefault();
    setCreating(true);
    setCreateError('');
    try {
      const resp = await paymentsAPI.create({
        member_id: Number(form.member_id),
        fee_year: Number(form.fee_year),
        fee: Number(form.fee),
        fee_type: 'AWTF',
        duedate: form.duedate || null,
      });
      setNewInvoice({ ...resp, memberName, fee: form.fee, fee_year: form.fee_year });
      setShowCreate(false);
      setForm({ member_id: '', fee_year: CURRENT_YEAR, fee: AWTF_FEE, duedate: '' });
      setMemberSearch('');
      setMemberName('');
      fetchPayments();
    } catch (e) {
      setCreateError(e.response?.data?.error || 'Failed to create invoice.');
    } finally {
      setCreating(false);
    }
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-3xl font-bold text-gray-800">Payments</h1>
        {activeTab === 'invoices' && (
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-1 py-1">
              <button type="button"
                onClick={() => setFeeYear((y) => {
                  const idx = availableYears.indexOf(y);
                  return idx >= 0 && idx < availableYears.length - 1 ? availableYears[idx + 1] : y - 1;
                })}
                className="flex h-8 w-8 items-center justify-center rounded-full text-slate-500 hover:bg-white hover:text-slate-800 font-bold text-lg transition-colors">
                ‹
              </button>
              <span className="min-w-[56px] text-center text-sm font-bold text-slate-800 select-none">{feeYear}</span>
              <button type="button"
                onClick={() => setFeeYear((y) => {
                  const idx = availableYears.indexOf(y);
                  return idx > 0 ? availableYears[idx - 1] : Math.min(y + 1, CURRENT_YEAR);
                })}
                disabled={feeYear >= CURRENT_YEAR}
                className="flex h-8 w-8 items-center justify-center rounded-full text-slate-500 hover:bg-white hover:text-slate-800 disabled:opacity-30 font-bold text-lg transition-colors">
                ›
              </button>
            </div>
            <select value={status} onChange={(e) => setStatus(e.target.value)}
              className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm">
              <option value="">All Status</option>
              <option value="PENDING">Pending</option>
              <option value="PAID">Paid</option>
            </select>
            <button type="button" onClick={() => { setShowCreate(true); setCreateError(''); setNewInvoice(null); }}
              className="flex items-center gap-2 rounded-2xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white shadow hover:bg-blue-700">
              <span className="text-lg leading-none">+</span> Generate Invoice
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="mb-6 flex border-b border-slate-200">
        {[
          { key: 'invoices', label: 'Invoices' },
          { key: 'direct-deposit', label: 'Direct Deposit' },
        ].map((t) => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            className={`px-5 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
              activeTab === t.key ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'direct-deposit' && <DirectDepositTab />}

      {activeTab === 'invoices' && (
      <>
      {/* Year summary cards */}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">Total Invoiced · {feeYear}</p>
          <p className="mt-1.5 text-2xl font-bold text-slate-800">{fmtTZS(summary.total_invoiced)}</p>
        </div>
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-widest text-emerald-600">Total Paid · {feeYear}</p>
          <p className="mt-1.5 text-2xl font-bold text-emerald-900">{fmtTZS(summary.total_paid)}</p>
        </div>
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-widest text-amber-600">Outstanding · {feeYear}</p>
          <p className="mt-1.5 text-2xl font-bold text-amber-900">{fmtTZS(summary.total_outstanding)}</p>
        </div>
      </div>

      {loading && <p className="text-gray-600">Loading...</p>}
      {error && !loading && <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 p-4 text-red-700">{error}</div>}

      {/* Table */}
      {!loading && !error && (
        <div className="overflow-hidden rounded-[28px] bg-white shadow-[0_20px_50px_rgba(15,23,42,0.08)]">
          {payments.length > 0 ? (
            <>
              <div className="hidden overflow-x-auto md:block">
                <table className="w-full">
                  <thead className="border-b bg-slate-100">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-slate-700">Full Name</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-700">Roll No</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-700">Reference No</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-700">Year</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-700">Amount</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-700">Due Date</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-700">Hali</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((p) => (
                      <tr key={p.id} className="border-b last:border-b-0 hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm font-medium text-slate-800">{p.member_full_name || `Member ${p.member_id}`}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{p.member_roll_no || '—'}</td>
                        <td className="px-4 py-3 font-mono text-sm font-semibold text-slate-900">{p.reference_no}</td>
                        <td className="px-4 py-3 text-sm text-slate-700">{p.fee_year}</td>
                        <td className="px-4 py-3 text-sm text-slate-700">TZS {Number(p.fee || 0).toLocaleString()}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{p.duedate || '-'}</td>
                        <td className="px-4 py-3">
                          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusBadge(p)}`}>
                            {String(p.paystatus || 'PENDING').toUpperCase()}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <Link to={`/payments/${p.id}`} className="font-medium text-blue-600 hover:underline text-sm">View</Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Mobile */}
              <div className="space-y-3 p-4 md:hidden">
                {payments.map((p) => (
                  <div key={p.id} className="rounded-[20px] border border-slate-200 bg-slate-50 p-4">
                    <p className="font-mono text-sm font-bold text-slate-900">{p.reference_no}</p>
                    <p className="text-xs text-slate-500">
                      {p.member_full_name || `Member ${p.member_id}`}
                      {p.member_roll_no && <span> · Roll {p.member_roll_no}</span>}
                      {' — '}{p.fee_year}
                    </p>
                    <div className="mt-2 flex items-center justify-between">
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusBadge(p)}`}>
                        {String(p.paystatus || 'PENDING').toUpperCase()}
                      </span>
                      <Link to={`/payments/${p.id}`} className="text-sm font-medium text-blue-600">View</Link>
                    </div>
                  </div>
                ))}
              </div>
              {totalPages > 1 && (
                <div className="flex items-center justify-between border-t border-slate-100 px-4 py-4">
                  <p className="text-sm text-slate-500">Page {currentPage} of {totalPages} · Total records {totalCount.toLocaleString()}</p>
                  <div className="flex gap-3">
                    <button type="button" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}
                      className="rounded-2xl bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200 disabled:opacity-50">
                      Previous
                    </button>
                    <button type="button" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                      className="rounded-2xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50">
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="p-8 text-center text-gray-600">No payments found.</div>
          )}
        </div>
      )}
      </>
      )}

      {/* Create Invoice Modal */}
      {showCreate && (
        <Modal title="Generate AWTF Invoice" onClose={() => setShowCreate(false)}>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Search Member</label>
              <div className="relative">
                <input type="text" value={memberSearch}
                  onChange={(e) => { setMemberSearch(e.target.value); setForm((f) => ({ ...f, member_id: '' })); setMemberName(''); }}
                  placeholder="Name or roll number..."
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required={!form.member_id} />
                {form.member_id && memberName && (
                  <div className="mt-1.5 flex items-center gap-2 rounded-xl bg-blue-50 px-3 py-2 text-sm text-blue-800">
                    <span>✓</span> <span className="font-medium">{memberName}</span>
                    <button type="button" onClick={() => { setForm((f) => ({ ...f, member_id: '' })); setMemberName(''); setMemberSearch(''); }}
                      className="ml-auto text-blue-400 hover:text-blue-700">✕</button>
                  </div>
                )}
                {memberSuggestions.length > 0 && !form.member_id && (
                  <ul className="absolute z-20 mt-1 w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg">
                    {memberSuggestions.slice(0, 6).map((m) => (
                      <li key={m.id}>
                        <button type="button"
                          onClick={() => { setForm((f) => ({ ...f, member_id: m.id })); setMemberName(`${m.full_name} (Roll: ${m.roll_no || 'N/A'})`); setMemberSearch(''); setMemberSuggestions([]); }}
                          className="w-full px-4 py-2.5 text-left text-sm hover:bg-slate-50">
                          <span className="font-medium">{m.full_name}</span>
                          <span className="ml-2 text-slate-400">Roll {m.roll_no || '-'}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Fee Year</label>
                <input type="number" value={form.fee_year} onChange={(e) => setForm((f) => ({ ...f, fee_year: e.target.value }))}
                  min="2000" max="2100" required
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Amount (TZS)</label>
                <input type="number" value={form.fee} onChange={(e) => setForm((f) => ({ ...f, fee: e.target.value }))}
                  min="1000" required
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Due Date (optional)</label>
              <input type="date" value={form.duedate} onChange={(e) => setForm((f) => ({ ...f, duedate: e.target.value }))}
                className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            {createError && <div className="rounded-2xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{createError}</div>}
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setShowCreate(false)}
                className="flex-1 rounded-2xl border border-slate-200 px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50">
                Cancel
              </button>
              <button type="submit" disabled={creating || !form.member_id}
                className="flex-1 rounded-2xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60">
                {creating ? 'Creating...' : 'Generate Invoice'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Success modal */}
      {newInvoice && (
        <Modal title="Invoice Created!" onClose={() => setNewInvoice(null)}>
          <div className="space-y-4">
            <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4 text-center">
              <p className="text-sm text-emerald-700 font-medium">Invoice created successfully</p>
              {newInvoice.memberName && <p className="mt-1 text-sm text-emerald-600">{newInvoice.memberName}</p>}
            </div>
            <div className="rounded-2xl border-2 border-blue-300 bg-blue-50 p-5 text-center">
              <p className="text-xs font-semibold uppercase tracking-widest text-blue-500">Reference Number</p>
              <p className="mt-2 font-mono text-3xl font-bold text-blue-900 tracking-wider">{newInvoice.reference_no}</p>
              <p className="mt-2 text-sm text-blue-700">TZS {Number(newInvoice.fee || 0).toLocaleString()} — Year {newInvoice.fee_year}</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm font-semibold text-slate-700">CRDB Payment Instructions:</p>
              <ol className="mt-2 space-y-1.5 text-sm text-slate-600 list-decimal list-inside">
                <li>Visit any CRDB Bank branch</li>
                <li>Provide reference number: <strong className="font-mono text-slate-800">{newInvoice.reference_no}</strong></li>
                <li>Pay TZS {Number(newInvoice.fee || 0).toLocaleString()}</li>
              </ol>
            </div>
            <button type="button" onClick={() => setNewInvoice(null)}
              className="w-full rounded-2xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white hover:bg-slate-800">
              Close
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function DirectDepositTab() {
  const [slips, setSlips]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [filter, setFilter]     = useState(''); // '' | 'pending' | 'paid'
  const [busyId, setBusyId]     = useState(null);
  const [feeYear, setFeeYear]   = useState(CURRENT_YEAR);
  const [availableYears, setAvailableYears] = useState([CURRENT_YEAR]);
  const [search, setSearch]         = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages]   = useState(1);
  const [totalCount, setTotalCount]   = useState(0);
  const [counts, setCounts]           = useState({ total: 0, pending: 0, paid: 0 });

  // Debounce free-text search so we don't hit the API on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    paymentSlipsAPI.list(feeYear, currentPage, ITEMS_PER_PAGE, search, filter)
      .then((resp) => {
        setSlips(resp.slips || []);
        setTotalPages(resp.total_pages || 1);
        setTotalCount(resp.total || 0);
        if (resp.counts) setCounts(resp.counts);
        if (resp.available_years?.length) setAvailableYears(resp.available_years);
      })
      .catch((e) => setError(e.response?.data?.error || 'Failed to load direct deposit slips.'))
      .finally(() => setLoading(false));
  }, [feeYear, currentPage, search, filter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setCurrentPage(1); }, [feeYear, search, filter]);

  async function handleMarkPaid(invoiceId) {
    if (!invoiceId) return;
    if (!confirm('Confirm you have verified the deposit slip and this invoice is paid?')) return;
    setBusyId(invoiceId);
    try {
      await paymentsAPI.markAsPaid(invoiceId);
      load();
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to mark as paid.');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div>
      {/* Deposit counts for the selected year */}
      <div className="mb-4 grid grid-cols-3 gap-3">
        <button type="button" onClick={() => setFilter('')}
          className={`rounded-2xl border p-4 text-left shadow-sm transition-colors ${
            filter === '' ? 'border-slate-800 bg-slate-800' : 'border-slate-200 bg-white hover:bg-slate-50'
          }`}>
          <p className={`text-xs font-semibold uppercase tracking-widest ${filter === '' ? 'text-slate-300' : 'text-slate-400'}`}>Total Deposits · {feeYear}</p>
          <p className={`mt-1.5 text-2xl font-bold ${filter === '' ? 'text-white' : 'text-slate-800'}`}>{counts.total.toLocaleString()}</p>
        </button>
        <button type="button" onClick={() => setFilter('pending')}
          className={`rounded-2xl border p-4 text-left shadow-sm transition-colors ${
            filter === 'pending' ? 'border-amber-500 bg-amber-500' : 'border-amber-200 bg-amber-50 hover:bg-amber-100'
          }`}>
          <p className={`text-xs font-semibold uppercase tracking-widest ${filter === 'pending' ? 'text-amber-50' : 'text-amber-600'}`}>Pending</p>
          <p className={`mt-1.5 text-2xl font-bold ${filter === 'pending' ? 'text-white' : 'text-amber-900'}`}>{counts.pending.toLocaleString()}</p>
        </button>
        <button type="button" onClick={() => setFilter('paid')}
          className={`rounded-2xl border p-4 text-left shadow-sm transition-colors ${
            filter === 'paid' ? 'border-emerald-600 bg-emerald-600' : 'border-emerald-200 bg-emerald-50 hover:bg-emerald-100'
          }`}>
          <p className={`text-xs font-semibold uppercase tracking-widest ${filter === 'paid' ? 'text-emerald-50' : 'text-emerald-600'}`}>Approved / Paid</p>
          <p className={`mt-1.5 text-2xl font-bold ${filter === 'paid' ? 'text-white' : 'text-emerald-900'}`}>{counts.paid.toLocaleString()}</p>
        </button>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-1 py-1">
          <button type="button"
            onClick={() => setFeeYear((y) => {
              const idx = availableYears.indexOf(y);
              return idx >= 0 && idx < availableYears.length - 1 ? availableYears[idx + 1] : y - 1;
            })}
            className="flex h-8 w-8 items-center justify-center rounded-full text-slate-500 hover:bg-white hover:text-slate-800 font-bold text-lg transition-colors">
            ‹
          </button>
          <span className="min-w-[56px] text-center text-sm font-bold text-slate-800 select-none">{feeYear}</span>
          <button type="button"
            onClick={() => setFeeYear((y) => {
              const idx = availableYears.indexOf(y);
              return idx > 0 ? availableYears[idx - 1] : Math.min(y + 1, CURRENT_YEAR);
            })}
            disabled={feeYear >= CURRENT_YEAR}
            className="flex h-8 w-8 items-center justify-center rounded-full text-slate-500 hover:bg-white hover:text-slate-800 disabled:opacity-30 font-bold text-lg transition-colors">
            ›
          </button>
        </div>
        <input type="text" value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search name, roll no, or reference..."
          className="min-w-[220px] flex-1 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
      </div>

      {loading && <p className="text-gray-600">Loading...</p>}
      {error && !loading && <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 p-4 text-red-700">{error}</div>}

      {!loading && !error && (
        <div className="overflow-hidden rounded-[28px] bg-white shadow-[0_20px_50px_rgba(15,23,42,0.08)]">
          {slips.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {slips.map((s) => {
                const paid = String(s.invoice_status || '').toUpperCase() === 'PAID';
                return (
                  <div key={s.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-slate-800">{s.full_name || `Member ${s.member_id}`}</span>
                        {s.roll_no && <span className="text-xs text-slate-400">Roll {s.roll_no}</span>}
                        <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                          paid ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                          {paid ? 'Approved / Paid' : 'Pending'}
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-slate-500">
                        <span className="font-mono">{s.reference_nos}</span>
                        {' · '}TZS {Number(s.amount || 0).toLocaleString()}
                        {' · '}Deposited on {s.date_paid || '—'} (self-reported)
                      </p>
                      {s.member_notes && <p className="mt-0.5 text-xs text-slate-400">{s.member_notes}</p>}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {s.attachment_path && (
                        <a href={s.attachment_path} target="_blank" rel="noreferrer"
                          className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50">
                          View Attachment
                        </a>
                      )}
                      {!paid && (
                        <button onClick={() => handleMarkPaid(s.invoice_id)} disabled={busyId === s.invoice_id}
                          className="rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-60 text-white px-3 py-2 text-xs font-semibold transition-colors">
                          {busyId === s.invoice_id ? 'Saving…' : 'Mark as Paid'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-8 text-center text-gray-600">No direct deposit slips found.</div>
          )}
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-slate-100 px-4 py-4">
              <p className="text-sm text-slate-500">Page {currentPage} of {totalPages} · Total records {totalCount.toLocaleString()}</p>
              <div className="flex gap-3">
                <button type="button" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}
                  className="rounded-2xl bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200 disabled:opacity-50">
                  Previous
                </button>
                <button type="button" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                  className="rounded-2xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50">
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-[28px] bg-white p-6 shadow-2xl">
        <div className="mb-5 flex items-center justify-between">
          <h2 className="text-xl font-bold text-slate-900">{title}</h2>
          <button type="button" onClick={onClose}
            className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}
